import { SalesforceUser } from '../types';

/**
 * Service for fetching Salesforce users from the backend
 */
class UserService {
  private users: SalesforceUser[] = [];
  private loading: boolean = false;
  private loaded: boolean = false;
  private error: Error | null = null;

  /**
   * Fetch users from API and cache them
   */
  async fetchUsers(): Promise<SalesforceUser[]> {
    // Return cached users if already loaded
    if (this.loaded && !this.error) {
      return this.users;
    }

    // Prevent multiple simultaneous fetches
    if (this.loading) {
      // Wait for the current fetch to complete
      while (this.loading) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      return this.users;
    }

    this.loading = true;
    this.error = null;

    try {
      const response = await fetch('/api/internal/users', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies for authentication
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch users: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (!result.success || !result.data) {
        throw new Error('Invalid response from users API');
      }

      this.users = result.data;
      this.loaded = true;
      return this.users;
    } catch (err) {
      this.error = err instanceof Error ? err : new Error('Unknown error fetching users');
      console.error('Error fetching users:', this.error);
      throw this.error;
    } finally {
      this.loading = false;
    }
  }

  /**
   * Get cached users (returns empty array if not loaded)
   */
  getCachedUsers(): SalesforceUser[] {
    return this.users;
  }

  /**
   * Clear the cache and force a refetch on next request
   */
  clearCache(): void {
    this.users = [];
    this.loaded = false;
    this.error = null;
  }

  /**
   * Get user by ID from cache
   */
  getUserById(userId: string): SalesforceUser | undefined {
    return this.users.find(u => u.id === userId);
  }

  /**
   * Format user for dropdown display: "Name (email)"
   */
  formatUserDisplay(user: SalesforceUser): string {
    return `${user.name} (${user.email})`;
  }
}

// Export singleton instance
export const userService = new UserService();

